import { Fragment, useCallback, useContext, useState } from "react";
import { SubHeaderLeft, SubHeaderRight } from "../../../layout/SubHeader/SubHeader";
import Card, { CardBody, CardTitle } from "../../../components/bootstrap/Card";
import Button from "../../../components/bootstrap/Button";
import { handleConfirmationAlert } from "../../../utils/ConfirmationAlert";
import ErrorMessage from "../../../components/ErrorMessage";
import { CustomTable } from "../../../components/table/CustomTable";
import { toast } from "react-toastify";
import useFetch from "../../../hooks/useFetch";
import useFilters from "../../../hooks/useFilters";
import { TaxesApiResponse } from "../../../type/tax-type";
import TaxFilters from "./taxes-options/TaxesFilters";
import TaxModalForm from "../create-tax/TaxModalForm";
import { TaxService } from "../../../services/taxes/taxService";
import { PrivilegeContext } from "../../../components/priviledge/PriviledgeProvider";

export interface ITaxFilters {
    active?: number;
};

const taxFilters: ITaxFilters = {
    active: 1,
};

const TaxesList = () => {

    const { userCan } = useContext(PrivilegeContext);

    const taxService = new TaxService();

    const { filters, updateFilters, resetFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters(taxFilters);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [show, setShow] = useState(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await taxService.listTaxes(filters);
        return response.getResponseData() as TaxesApiResponse;
    }, [filters]));

    const handleDelete = async (id: string) => {
        try {
            const response = await (await taxService.deleteTax(id)).getResponseData();
            if (response.success) {
                refetch();
                setTimeout(() => {
                    toast.success('Impuesto eliminado correctamente');
                }, 100);
            } else {
                toast.error(response.message || "Error al eliminar el impuesto");
            }
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    const _onCloseModal = (type: number, message?: string, errors?: any) => {
        if (type === 1) {
            setSelectedItem(null);
            setShow(false);
            setModalOpen(false);
            refetch();
            toast.success(message);
        } else if (type === 0) {
            message !== '' && toast.error(message);
            errors && errors.forEach((error: any) => {
                toast.error(error.message);
            });
        }
    };

    const getContent = () => {
        if (error) return <ErrorMessage error={error} />;

        if (data) {
            let apiData = data as TaxesApiResponse;

            return (
                <CustomTable
                    title="Impuestos"
                    loading={loading}
                    data={apiData ? apiData.taxes : null}
                    pagination={true}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: apiData as TaxesApiResponse ? apiData.lastPage : 1,
                        handlePagination: (page: any) => {
                            updatePage({ selected: page.selected + 1 });
                        },
                        handlePerPage: updatePageSize,
                    }}
                    className={"table-striped table-hover"}
                    columns={[
                        {
                            name: "Nombre",
                            keyValue: "name",
                            className: "text-center",
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                return (
                                    <div className="cursor-pointer text-primary fw-bold text-center" onClick={() => {
                                        setSelectedItem(element);
                                        setModalOpen(true);
                                        setShow(true);
                                    }}>
                                        {element.name}
                                    </div>
                                )
                            },
                        },
                        {
                            name: "Porcentaje",
                            keyValue: "value",
                            className: "text-center",
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                return (
                                    <div className="text-center">
                                        {element.value}
                                    </div>
                                )
                            },

                        },
                        { name: "Acciones", className: "min-w-100px text-end", isActionCell: true }
                    ]}
                    actions={
                        [
                            {
                                title: "Editar",
                                buttonType: 'icon',
                                icon: "Edit",
                                additionalClasses: `text-primary ${userCan("edit", "budget_taxes") ? "" : "d-none"}`,
                                description: "Editar impuesto",
                                callback: (item: any) => {
                                    if (!userCan("edit", "budget_taxes")) return;
                                    setSelectedItem(item);
                                    setModalOpen(true);
                                    setShow(false);
                                },
                            },
                            {
                                title: "Eliminar",
                                buttonType: 'icon',
                                icon: "Delete",
                                additionalClasses: `text-danger ${userCan("delete", "budget_taxes") ? "" : "d-none"}`,
                                description: "Eliminar impuesto",
                                callback: (item: any) => {
                                    if (!userCan("delete", "budget_taxes")) return;
                                    handleConfirmationAlert({
                                        title: "Eliminar impuesto",
                                        text: "¿Está seguro que desea eliminar el impuesto?",
                                        icon: "warning",
                                        onConfirm: () => {
                                            handleDelete(item.id);
                                        }
                                    })
                                },
                            },
                            {
                                title: "Sin permisos",
                                buttonType: 'icon',
                                icon: "Block",
                                additionalClasses: `text-danger ${(userCan('edit', 'budget_taxes') || userCan('delete', 'budget_taxes')) ? 'd-none' : ''}`,
                                description: "No tiene permisos para editar ni eliminar",
                                callback: (item: any) => { },
                            }
                        ]}
                />
            );
        }
    };

    return (
        <Fragment>
            <Card stretch>
                <div className="row m-4 d-flex justify-content-between">
                    <SubHeaderLeft>
                        <CardTitle>Impuestos</CardTitle>
                    </SubHeaderLeft>
                    <SubHeaderRight>
                        <TaxFilters filters={filters} updateFilters={updateFilters} updatePageSize={updatePageSize} resetFilters={resetFilters} />
                        <Button
                            color="primary"
                            title="Crear impuesto"
                            icon="Add"
                            isLight
                            className={userCan("create", "budget_taxes") ? "" : "d-none"}
                            onClick={() => {
                                if (!userCan("create", "budget_taxes")) return;
                                setModalOpen(true);
                                setSelectedItem(null);
                                setShow(false);
                            }}
                        />
                    </SubHeaderRight>
                </div>
                <CardBody className="table-responsive">
                    <Fragment>{getContent()}</Fragment>
                </CardBody>
            </Card>

            {modalOpen && (<TaxModalForm isOpen={modalOpen} setIsOpen={setModalOpen} onClose={_onCloseModal} data={selectedItem} show={show} />)}
        </Fragment>
    );
};

export default TaxesList;